import { terrain } from '../tunables.js';
import type { RouteDef } from './route.js';

/**
 * The ground, as a seeded height sample array.
 *
 * The whole point of this module is one invariant:
 *
 *   **No segment of the terrain is ever steeper than `terrain.maxSlope`.**
 *
 * That is how "non-flat, but not too hard for a three-year-old to drive" becomes true *by
 * construction* rather than by playtesting and hoping. An unclimbable hill cannot exist, so she can
 * never get wedged somewhere and give up. The clamp applies to everything, ramps included - a ramp
 * gets its airtime from being short and abrupt, not from being steep enough to strand her.
 *
 * Pure: seeded PRNG, no Math.random, no clock. Same seed always gives the same ground, which is what
 * makes the return leg exactly the outbound leg reversed.
 */

export interface Heightfield {
  /** World x of `heights[0]`, in metres. Negative: the field starts behind the Lookout. */
  readonly startX: number;
  readonly spacingM: number;
  readonly heights: readonly number[];
  readonly endX: number;
}

/** mulberry32: small, fast, well-distributed, and deterministic from a single integer seed. */
function mulberry32(seed: number): () => number {
  let state = seed | 0;
  return () => {
    state = (state + 0x6d2b79f5) | 0;
    let t = Math.imul(state ^ (state >>> 15), 1 | state);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Smoothstep, so interpolated noise has no visible kinks at the sample points. */
function smooth(t: number): number {
  return t * t * (3 - 2 * t);
}

/**
 * Value noise: one random value per grid point, smoothly interpolated between. Cheaper than Perlin
 * and entirely good enough for rolling ground.
 */
function valueNoise(values: readonly number[], position: number): number {
  const index = Math.floor(position);
  const fraction = position - index;
  const a = values[((index % values.length) + values.length) % values.length] as number;
  const b = values[(((index + 1) % values.length) + values.length) % values.length] as number;
  return a + (b - a) * smooth(fraction);
}

export function generateHeightfield(route: RouteDef): Heightfield {
  const spacingM = terrain.sampleSpacing / 100; // tunables carry pixels; the field works in metres
  const startX = -route.flatMarginM;
  const endX = route.lengthM + route.flatMarginM;
  const count = Math.max(2, Math.ceil((endX - startX) / spacingM) + 1);

  const random = mulberry32(route.seed);
  // Two octaves: one at the hill wavelength, one at a third of it for variation.
  const coarse = Array.from({ length: 32 }, () => random() * 2 - 1);
  const fine = Array.from({ length: 64 }, () => random() * 2 - 1);

  const heights: number[] = [];
  for (let i = 0; i < count; i++) {
    const x = startX + i * spacingM;

    const coarseValue = valueNoise(coarse, x / route.hillWavelengthM);
    const fineValue = valueNoise(fine, x / (route.hillWavelengthM / 3));
    let height =
      coarseValue * (route.hillAmplitudeM / 2) +
      fineValue * (route.hillAmplitudeM / 2) * route.roughness;

    height += rampHeightAt(route, x);

    // Ease the ground back to exactly zero at both ends, so the Lookout and the goal always sit on
    // the level and the car never spawns on a slope.
    heights.push(height * endTaper(route, x));
  }

  clampSlopes(heights, terrain.maxSlope * spacingM);

  return { startX, spacingM, heights, endX: startX + (count - 1) * spacingM };
}

/** A short, abrupt bump. Kept inside the slope clamp; the hop comes from its shortness. */
function rampHeightAt(route: RouteDef, x: number): number {
  let total = 0;
  for (const ramp of route.ramps) {
    const t = (x - ramp.atM) / ramp.lengthM;
    if (t < 0 || t > 1) continue;
    // Rises to the full height at the lip, then drops away - so she launches off the end.
    total += ramp.heightM * smooth(t);
  }
  return total;
}

/** 0 at the very ends, 1 once past the flat margin. */
function endTaper(route: RouteDef, x: number): number {
  const margin = Math.max(0.001, route.flatMarginM);
  if (x <= 0) return 0;
  if (x >= route.lengthM) return 0;
  const fromStart = Math.min(1, x / margin);
  const fromEnd = Math.min(1, (route.lengthM - x) / margin);
  return smooth(Math.min(fromStart, fromEnd));
}

/**
 * Enforce |h[i+1] - h[i]| <= maxDelta, in two sweeps.
 *
 * This is the standard Lipschitz clamp: a forward pass limits how fast the ground can rise, a
 * backward pass limits how fast it can fall, and because both passes only ever *lower* a sample
 * neither can reintroduce a violation the other has fixed. Two passes are provably sufficient - no
 * iterating to convergence, and no chance of a stray steep segment surviving.
 */
export function clampSlopes(heights: number[], maxDelta: number): void {
  for (let i = 1; i < heights.length; i++) {
    heights[i] = Math.min(heights[i] as number, (heights[i - 1] as number) + maxDelta);
  }
  for (let i = heights.length - 2; i >= 0; i--) {
    heights[i] = Math.min(heights[i] as number, (heights[i + 1] as number) + maxDelta);
  }
}

/** Ground height at an arbitrary x, linearly interpolated. Flat beyond either end. */
export function heightAt(field: Heightfield, x: number): number {
  const position = (x - field.startX) / field.spacingM;
  if (position <= 0) return field.heights[0] ?? 0;
  if (position >= field.heights.length - 1) return field.heights[field.heights.length - 1] ?? 0;

  const index = Math.floor(position);
  const fraction = position - index;
  const a = field.heights[index] as number;
  const b = field.heights[index + 1] as number;
  return a + (b - a) * fraction;
}

/** Signed gradient (rise over run) at x. Positive means uphill in the +x direction. */
export function slopeAt(field: Heightfield, x: number): number {
  const half = field.spacingM / 2;
  return (heightAt(field, x + half) - heightAt(field, x - half)) / field.spacingM;
}

/** The steepest segment in the field. Used by the tests that police the invariant. */
export function maxAbsSlope(field: Heightfield): number {
  let steepest = 0;
  for (let i = 1; i < field.heights.length; i++) {
    const delta = (field.heights[i] as number) - (field.heights[i - 1] as number);
    steepest = Math.max(steepest, Math.abs(delta) / field.spacingM);
  }
  return steepest;
}

/** Surface points in world metres, for building collision geometry and for rendering. */
export function surfacePoints(field: Heightfield): readonly { x: number; y: number }[] {
  return field.heights.map((y, i) => ({ x: field.startX + i * field.spacingM, y }));
}
